import { Promise as BluebirdPromise } from 'bluebird';
import cns from 'classnames';
import { get, isEmpty, isEqual } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { Col } from 'react-bootstrap';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { change, Field, getFormValues, isDirty, reduxForm, SubmissionError } from 'redux-form';
import {
    postSearchedCompanyMemberProfiles,
    putCompany,
    putCompanyMembers
} from '../../../../../../shared/actions/Companies';
import { getManyProfiles, realDeleteProfile } from '../../../../../../shared/actions/UserProfiles';
import { makeGetObjectsFromStateById } from '../../../../../../shared/selectors/index';
import { getCompanyMembers, getCompanyMembersCount } from '../../../../../../shared/utils/FetchUtils/CompanyFetchUtils';

import { RuntimeContext } from '../../../../../../../common/RuntimeContext';
import {
    clearSearchedProfiles,
    getManyTeamsAdmin,
    hideModal,
    inviteEmployees,
    showModal
} from '../../../../../../shared/actions';
import BasicSelect from '../../../../../../shared/components/formComponents/groups/BasicSelect';
import Warning from '../../../../../../shared/components/formComponents/Warning';
import PaginatedFetch from '../../../../../../shared/components/PaginatedFetch';
import VerbButton, { VERB_BUTTON_TYPE } from '../../../../../../shared/components/VerbButton';
import VerbText from '../../../../../../shared/components/VerbText';
import { MODALS } from '../../../../../../shared/Constants';
import extractEmails from '../../../../../../shared/utils/ExtractEmails';
import ComparisonBar from '../../../../../components/ComparisonBar';
import InvitedMembersTab from './InvitedMembersTab';
import RegisteredMembersTab from './RegisteredMembersTab';

class CompanyEmployeesForm extends Component {
    constructor(props) {
        super(props);
        this.state = {
            company: {},
            updatingMembers: false,
            failMsg: 'Request failed',
            inviteSuccessMsg: 'Success!',
            emailSentCount: 0,
            membersToAdd: [],
            selectedTab: 'registered',
            membersAdded: 0,
            registeredPage: 0,
            invitedPage: 0,
            registeredMembers: [],
            invitedMembers: [],
            memberCounts: {
                invited: 0,
                registered: 0,
                members: 0,
                awaitingRegistration: 0
            },
            hasSearchFilter: false,
            hits: {},
            fetchLimit: 10,
            isClearingSearch: false,
            currentSearchQuery: ''
        };
    }

    static propTypes = {
        company: PropTypes.object.isRequired,
        licenseNumber: PropTypes.number,
        inviteEmployees: PropTypes.func,
        isVerbAdmin: PropTypes.bool,
        isAccountOwner: PropTypes.bool,
        putCompanyMembers: PropTypes.func,
        getManyProfiles: PropTypes.func,
        sendEmailReminders: PropTypes.func,
        allProfiles: PropTypes.arrayOf(PropTypes.object),
        currentEmployeeInviteFormValues: PropTypes.object,
        showModal: PropTypes.func,
        hideModal: PropTypes.func,
        profilesById: PropTypes.object,
        postSearchedCompanyMemberProfiles: PropTypes.func,
        realDeleteProfile: PropTypes.func,
        changeFormValue: PropTypes.func,
        dirty: PropTypes.bool,
        toggleShowUnsavedChangesPrompt: PropTypes.func,
        getManyTeamsAdmin: PropTypes.func,
        changeSearchFormValue: PropTypes.func,
        addWarning: PropTypes.func,
        toggleConfirmationModal: PropTypes.func,
        tabId: PropTypes.string,
        tabWarnings: PropTypes.array
    };

    static contextType = RuntimeContext;

    componentDidMount() {
        const { isAccountOwner, isVerbAdmin, getManyProfiles, company, tabId, addWarning } = this.props;

        if (isEmpty(company)) {
            return null;
        }

        if (isVerbAdmin) {
            return Promise.all([
                this._getMemberData(),
                getManyProfiles({ noCompanyAffiliation: true, pathsContext: this.context.pathsWeb })
            ]);
        }
        if (isAccountOwner) {
            return Promise.all([this._getMemberData()]);
        }

        addWarning(tabId, []);
        return null;
    }

    componentDidUpdate(prevProps, prevState) {
        if (!prevProps.isVerbAdmin && this.props.isVerbAdmin) {
            return this.props.getManyProfiles({ noCompanyAffiliation: true, pathsContext: this.context.pathsWeb });
        }

        if (!isEmpty(this.props.company) && prevState.selectedTab !== this.state.selectedTab) {
            return this.getCompanyMembersByTabName({ clearSearch: true });
        }
        if (isEmpty(prevProps.company) && !isEmpty(this.props.company)) {
            if (this.props.isVerbAdmin) {
                return Promise.all([
                    this._getMemberData(),
                    getManyProfiles({ noCompanyAffiliation: true, pathsContext: this.context.pathsWeb })
                ]);
            }
            if (this.props.isAccountOwner) {
                return Promise.all([this._getMemberData()]);
            }
        }
        if (prevProps.dirty !== this.props.dirty) {
            return this.props.toggleShowUnsavedChangesPrompt(this.props.dirty);
        }
        return null;
    }

    _getMemberData = () => Promise.all([this.getCompanyMembersByTabName(), this._getCompanyMembersCount()]);

    _getCompanyMembersCount = () => {
        const { company } = this.props;
        return getCompanyMembersCount({ companyId: company._id, pathsContext: this.context.pathsWeb }).then(
            (result) => {
                this.setState({
                    memberCounts: {
                        invited: result?.totalInvited || 0,
                        registered: result?.totalRegistered || 0,
                        members: result?.totalMembers || 0,
                        awaitingRegistration: result?.awaitingRegistration || 0
                    }
                });
            }
        );
    };

    getCompanyMembersByTabName = (options = {}) => {
        const { clearSearch = false } = options;
        const { company } = this.props;
        const { selectedTab } = this.state;
        const sortDescending = selectedTab === 'registered';
        const params = {
            companyId: company._id,
            query: {
                page: clearSearch ? 0 : this.state[`${selectedTab}Page`],
                [`${selectedTab}Order`]: sortDescending ? 'desc' : 'asc',
                loadRemaining: this.state[`${selectedTab}Members`]?.length
                    ? this.state[`${selectedTab}Members`].length >= 10
                    : false
            },
            pathsContext: this.context.pathsWeb
        };
        return getCompanyMembers(params).then((userProfiles) => {
            if (clearSearch) {
                return this.setState({
                    [`${selectedTab}Members`]: userProfiles,
                    [`${selectedTab}Page`]: 1,
                    hasSearchFilter: false
                });
            }
            return this._addUsersToStateByTabName(selectedTab, userProfiles);
        });
    };

    _addUsersToStateByTabName = (selectedTab, userProfiles, options = {}) => {
        const { addToTop } = options;
        this.setState((prevState) => {
            const previousMembers = prevState && prevState[`${selectedTab}Members`];
            const previousPage = prevState && prevState[`${selectedTab}Page`];
            let newUserProfiles;
            if (addToTop) {
                newUserProfiles = userProfiles.concat(previousMembers);
            } else {
                newUserProfiles = previousMembers.concat(userProfiles);
            }

            return {
                [`${selectedTab}Members`]: newUserProfiles,
                [`${selectedTab}Page`]: previousPage + 1
            };
        });
        return userProfiles;
    };

    // eslint-disable-next-line no-alert
    confirm = () => window.confirm('Are you sure you want to delete?');

    openInviteEmployees = () => {
        const {
            company: { enableInviteByEmail = true },
            isVerbAdmin
        } = this.props;
        this.props.showModal({
            modalType: MODALS.INVITE_EMPLOYEES,
            modalProps: {
                className: 'admin-modal',
                onClose: this.props.hideModal(),
                onCancel: this.props.hideModal(),
                onSubmit: this.handleInviteEmployeesSubmit,
                failMsg: 'Request failed',
                companyPermalink: get(this.props.company, 'permalink', ''),
                enableInviteByEmail,
                isVerbAdmin
            }
        });
    };

    openBulkEmployeeInviteModal = () => {
        const {
            company: { enableInviteByEmail = true },
            toggleConfirmationModal
        } = this.props;
        this.props.showModal({
            modalType: MODALS.BULK_INVITE_MODAL,
            modalProps: {
                className: 'bulk-invite-modal',
                onCancel: this.props.hideModal(),
                handleSubmit: this.handleBulkInviteEmployeesSubmit,
                enableInviteByEmail,
                toggleConfirmationModal
            }
        });
    };

    removeMembersFromCompany = (membersToDelete) => {
        const confirmation = this.confirm('Are you sure you want to remove these members?');
        if (!confirmation) {
            return BluebirdPromise.resolve();
        }
        const { company, putCompanyMembers, getManyTeamsAdmin } = this.props;

        return putCompanyMembers({
            companyId: company._id,
            action: 'remove',
            membersToUpdate: membersToDelete,
            pathsContext: this.context.pathsWeb
        })
            .then(() =>
                getManyTeamsAdmin({
                    parentId: company._id,
                    getRegistered: false,
                    getAllMembers: true,
                    pathsContext: this.context.pathsWeb
                })
            )
            .then(() => this._removeMembers(membersToDelete));
    };

    _removeMembers = (memberToDelete) => {
        const membersDict = {};
        for (let i = 0; i < memberToDelete.length; i += 1) {
            const id = memberToDelete[i];
            membersDict[id] = true;
        }
        return this.setState((prevstate) => {
            const { registeredMembers } = prevstate;
            const newRegisteredMembers = [];
            for (let i = 0; i < registeredMembers.length; i += 1) {
                if (!membersDict[registeredMembers[i]._id]) {
                    newRegisteredMembers.push(registeredMembers[i]);
                }
            }
            return { registeredMembers: newRegisteredMembers };
        });
    };

    sendEmailReminders = (membersToEmail) => {
        const { sendEmailReminders } = this.props;

        if (sendEmailReminders) {
            return sendEmailReminders(membersToEmail).then((result) => {
                this.setState({
                    membersToEmail: [],
                    emailSentCount: result.sentCount
                });
            });
        }
        return null;
    };

    handleInviteEmployeesSubmit = () => {
        const { inviteEmployees, company, currentEmployeeInviteFormValues } = this.props;
        const { invitedMembers, fetchLimit } = this.state;

        let membersToEmail = extractEmails(currentEmployeeInviteFormValues.emails);
        const companyPermalink = get(company, 'permalink', '');
        if (!membersToEmail || !membersToEmail.length > 0) {
            return Promise.reject(new SubmissionError({ emails: 'No valid emails' }));
        }

        membersToEmail = membersToEmail.map((email) => email.toLowerCase());
        const inviteParams = {
            companyId: company._id,
            permalink: companyPermalink,
            emailList: membersToEmail,
            pathsContext: this.context.pathsWeb
        };

        let confirmation = true;

        if (currentEmployeeInviteFormValues?.inviteAsCompanyAdmin) {
            inviteParams.inviteAsCompanyAdmin = true;
            confirmation = window.confirm(
                `Are you sure you want to invite the folowing users ${membersToEmail.join(',')} as Company Admins?`
            );
        }
        if (confirmation) {
            return inviteEmployees(inviteParams).then((statusList) => {
                const successList = [];
                if (statusList && statusList.recipients) {
                    statusList.recipients.forEach((user) => {
                        successList.push(user.address.email);
                    });
                }
                if (!statusList || (statusList && statusList.result.total_accepted_recipients === 0)) {
                    throw new SubmissionError({ emails: `Emails failed to send.`, _error: 'Email failed' });
                } else {
                    // append newly invited member to displayed list
                    if (invitedMembers.length < 10) {
                        this.setState(({ invitedMembers }) => ({
                            invitedMembers: [
                                ...invitedMembers,
                                ...statusList.recipients.map((user) => ({
                                    email: user?.address?.email,
                                    createdOn: new Date(Date.now()).toISOString()
                                }))
                            ].slice(0, fetchLimit)
                        }));
                    }
                    return {
                        inviteSuccessMsg: `Invites successfully sent to ${
                            successList.length
                        } employee(s): ${successList.join(', ')}${
                            currentEmployeeInviteFormValues?.inviteAsCompanyAdmin ? 'as Company Admin' : ''
                        }.`
                    };
                }
            });
        }
        return Promise.resolve();
    };

    handleBulkInviteEmployeesSubmit = async (usersToInvite) => {
        const { inviteEmployees, company, addWarning, tabId, tabWarnings = [] } = this.props;
        const { invitedMembers, fetchLimit } = this.state;

        const companyPermalink = get(company, 'permalink', '');
        if (!usersToInvite || !usersToInvite.length > 0) {
            return Promise.reject(new SubmissionError({ emails: 'No valid emails' }));
        }

        const successList = [];
        const membersToEmail = usersToInvite.map((email) => email.toLowerCase());
        const inviteParams = {
            companyId: company._id,
            permalink: companyPermalink,
            emailList: membersToEmail,
            csvBulkInvite: true,
            pathsContext: this.context.pathsWeb
        };

        const range = 500;
        let rangeLower = 0;
        let rangeUpper = membersToEmail.length > 500 ? 500 : membersToEmail.length;
        let requestFailed = false;

        while (rangeUpper <= membersToEmail.length) {
            inviteParams.emailList = membersToEmail.slice(rangeLower, rangeUpper);
            if (!inviteParams.emailList.length) {
                break;
            }
            try {
                // eslint-disable-next-line no-await-in-loop
                const statusList = await inviteEmployees(inviteParams);
                if (statusList && statusList.recipients) {
                    statusList.recipients.forEach((user) => {
                        successList.push(user.address.email);
                    });
                }
                if (!statusList || (statusList && statusList?.result?.total_accepted_recipients === 0)) {
                    const warnings = [
                        ...tabWarnings,
                        <Warning
                            messageTextProp={{
                                messageId: 'BULK_INVITE_FAILURE',
                                defaultMessage:
                                    'An error occured during the invitation process. Please resubmit your CSV Bulk Invite.'
                            }}
                            warningColor={'critical-warning'}
                            id={'BULK_INVITE_FAILURE'}
                        />
                    ];
                    addWarning(tabId, warnings);
                    requestFailed = true;
                    break;
                }
                if (rangeUpper + range < membersToEmail.length) {
                    rangeLower = rangeUpper;
                    rangeUpper += range;
                } else {
                    rangeLower = rangeUpper;
                    rangeUpper = membersToEmail.length;
                }
            } catch (err) {
                const warnings = [
                    ...tabWarnings,
                    <Warning
                        messageTextProp={{
                            messageId: 'BULK_INVITE_FAILURE',
                            defaultMessage:
                                'An error occured during the invitation process. Please resubmit your CSV Bulk Invite.'
                        }}
                        warningColor={'critical-warning'}
                        id={'BULK_INVITE_FAILURE'}
                    />
                ];
                addWarning(tabId, warnings);
                return 'failed';
            }
        }

        if (requestFailed) {
            return 'failed';
        }

        // append newly invited member to displayed list
        if (invitedMembers.length < 10) {
            this.setState(({ invitedMembers }) => ({
                invitedMembers: [
                    ...invitedMembers,
                    ...successList.map((email) => ({
                        email,
                        createdOn: new Date(Date.now()).toISOString()
                    }))
                ].slice(0, fetchLimit)
            }));
        }
        const warnings = [
            ...tabWarnings,
            <Warning
                messageTextProp={{
                    messageId: 'BULK_INVITE_SUCCESS',
                    defaultMessage: '{numInvitedUsers} learners have successfully been invited to {companyName}.'
                }}
                warningColor={'success-warning'}
                valuesToInject={{ companyName: company.name, numInvitedUsers: usersToInvite.length }}
                id={'BULK_INVITE_SUCCESS'}
            />
        ];
        addWarning(tabId, warnings);
        return 'success';
    };

    addMembersToCompany = () => {
        const { membersToAdd, selectedTab } = this.state;
        const { company, putCompanyMembers, profilesById, changeFormValue } = this.props;
        if (!membersToAdd.length) {
            return BluebirdPromise.resolve();
        }
        const mappedMembers = membersToAdd.map((id) => profilesById[id]);
        this.setState({ updatingMembers: true, membersAdded: membersToAdd.length });

        return putCompanyMembers({
            companyId: company._id,
            action: 'add',
            membersToUpdate: membersToAdd,
            pathsContext: this.context.pathsWeb
        }).then(() => {
            this.setState({ updatingMembers: false, membersToAdd: [] });
            changeFormValue({ field: 'memberIds', value: [] });
            this._addUsersToStateByTabName(selectedTab, mappedMembers, { addToTop: true });
        });
    };

    addMember = (event, newValues, oldValues) => {
        const newTrimmedValues = newValues.map((member) => member.value);
        if (!isEqual(newTrimmedValues, oldValues)) {
            this.setState({ membersToAdd: newTrimmedValues });
        }
    };

    getMemberOptions = () => {
        const userProfiles = [];
        const { allProfiles } = this.props;

        allProfiles.forEach((profile) => {
            // only show users if they aren't assigned to a company
            const profileCompanyId = (profile && profile.companyId) || null;
            if (!profileCompanyId) {
                userProfiles.push({
                    value: profile._id,
                    label: `${profile.firstName} ${profile.lastName} - (${profile.email})`
                });
            }
        });

        return userProfiles;
    };

    setTab = (tabname) => () => this.setState({ selectedTab: tabname, currentSearchQuery: '' });

    handleCompanyMemberSearch = (hits, queryParams = { query: '' }) => {
        const { fetchLimit, selectedTab } = this.state;
        const { postSearchedCompanyMemberProfiles } = this.props;
        if (hits && hits._id && hits._id.length) {
            const { company } = this.props;
            const page = !hits || isEmpty(hits) ? this.state[`${selectedTab}Page`] : 1; // fetch searchPage 1 when hits are cleared out
            const body = {
                companyId: company._id,
                ...hits,
                limit: fetchLimit,
                page,
                sortField: `${selectedTab}DateTime`,
                sortOrder: -1,
                pathsContext: this.context.pathsWeb
            };

            if (selectedTab === 'registered') {
                body.getRegistered = true;
            } else {
                body.getInvitedAndUnregistered = true;
            }
            return postSearchedCompanyMemberProfiles(body).then((userProfiles) =>
                this.setState({
                    [`${selectedTab}Members`]: userProfiles,
                    hasSearchFilter: true,
                    hits,
                    [`${selectedTab}Page`]: page,
                    currentSearchQuery: queryParams.query
                })
            );
        }

        // This is horrible and I'm sorry. Our search returns an empty array if search is not executed.
        // Otherwise it returns an object.
        const searchReturnedDefaultArray = Array.isArray(hits);
        const searchFoundNoResults = !searchReturnedDefaultArray && hits && hits._id && Array.isArray(hits._id);
        const setStateParams = {
            hits: {},
            hasSearchFilter: !Array.isArray(hits)
        };

        if (searchFoundNoResults) {
            setStateParams[`${selectedTab}Members`] = [];
        }
        return this.setState(setStateParams);
    };

    getMoreSearchedCompanyMemberProfiles = () => {
        const { company, postSearchedCompanyMemberProfiles } = this.props;
        const { hits, selectedTab, fetchLimit } = this.state;
        const body = {
            companyId: company._id,
            page: this.state[`${selectedTab}Page`] + 1,
            limit: fetchLimit,
            sortField: `${selectedTab}DateTime`,
            sortOrder: -1,
            pathsContext: this.context.pathsWeb,
            ...hits
        };

        if (selectedTab === 'registered') {
            body.getRegistered = true;
        } else {
            body.getInvitedAndUnregistered = true;
        }

        return postSearchedCompanyMemberProfiles(body).then((userProfiles) => {
            this._addUsersToStateByTabName(selectedTab, userProfiles);
            return userProfiles;
        });
    };

    handleClearSearchBar = (params = {}) => {
        const { searchVal } = params;
        const { selectedTab } = this.state;
        if (!searchVal) {
            this.setState(
                {
                    [`${selectedTab}Page`]: 0,
                    hasSearchFilter: false,
                    hits: {}
                },
                () => this.getCompanyMembersByTabName({ clearSearch: true })
            );
        }
    };

    clearSearch = () => {
        this.props.changeSearchFormValue({
            field: 'searchVal',
            value: ''
        });
        this.handleClearSearchBar();
        this.setState({ currentSearchQuery: '' });
    };

    render() {
        const { licenseNumber, isVerbAdmin, company, realDeleteProfile } = this.props;
        const { registeredMembers, memberCounts, invitedMembers } = this.state;
        const { enableInviteByEmail = true } = company;
        const {
            updatingMembers,
            membersToAdd,
            membersAdded,
            emailSentCount,
            selectedTab,
            hasSearchFilter,
            hits
        } = this.state;
        const searchHasEmptyResults = hasSearchFilter && (!hits || !hits._id || !hits._id.length);
        return (
            <div>
                <form className={'companyForm'}>
                    <div className={'container'}>
                        <div className={'row'}>
                            <div className={'form-action-buttons formButtons me-auto invite-form-action-buttons'}>
                                <VerbButton
                                    className={'invite-btn btn btn-default'}
                                    onClick={this.openInviteEmployees}
                                    fontAwesomeIcon={'fa-plus'}
                                    buttonText={'INVITE'}
                                />
                                <VerbButton
                                    className={'invite-btn btn'}
                                    onClick={this.openBulkEmployeeInviteModal}
                                    fontAwesomeIcon={'fa-plus'}
                                    buttonText={'CSV BULK INVITE'}
                                    buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                                />
                            </div>
                        </div>
                        <br />
                        <div className={'row comparison-container'}>
                            <div className={enableInviteByEmail ? 'col-sm-6' : 'col-sm-12'}>
                                <ComparisonBar
                                    isVisible={true}
                                    divisor={memberCounts.members}
                                    dividend={licenseNumber}
                                    dividendLabelTextId={'BILLING_LICENSE_COUNT'}
                                    divisorLabelTextId={'COMPANY_LICENSE_USED_LABEL'}
                                    headingTextId={'HEADING_ACCOUNT_USAGE'}
                                    headingDefaultMessage={'ACCOUNT USAGE'}
                                    labelString={`${memberCounts.members} of ${licenseNumber} licenses used`}
                                />
                            </div>
                            {enableInviteByEmail && (
                                <div className={'col-sm-6'}>
                                    <ComparisonBar
                                        isVisible={true}
                                        dividend={memberCounts.invited}
                                        dividendLabelTextId={'COMPANY_INVITED_LABEL'}
                                        divisorLabelTextId={'COMPANY_LICENSE_REGISTERED_LABEL'}
                                        divisor={memberCounts.registered}
                                        headingTextId={'HEADING_RESPONSE_RATE'}
                                        headingDefaultMessage={'RESPONSE RATE'}
                                        labelString={`${memberCounts.registered} of ${memberCounts.invited} invitees registered`}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <br />
                    <br />
                    {isVerbAdmin ? (
                        <div className={'col-sm-12 invite-members-container'}>
                            <Col xs={12}>
                                <label className={'employees-form-label'}>Add Members</label>
                            </Col>
                            <div className={'col-sm-12 add-member-instructions'}>
                                <div className={'col-sm-6'}>
                                    <small>
                                        <VerbText
                                            messageId={'COMPANY_ADD_MEMBER_INSTRUCTIONS'}
                                            defaultMessage={'Select members below to add to your organization.'}
                                        />
                                    </small>
                                </div>
                                <div className={'col-sm-4 form-action-buttons formButtons'}>
                                    <VerbButton
                                        buttonType={VERB_BUTTON_TYPE.SECONDARY_DEFAULT}
                                        onClick={this.addMembersToCompany}
                                        fontAwesomeIcon={'fal fa-user'}
                                        disabled={!membersToAdd.length}
                                        textId={'ACTION_ADD_MEMBERS'}
                                        defaultMessage={'ADD MEMBERS'}
                                        className={'ms-auto'}
                                    />
                                </div>
                            </div>
                            <div className={cns('ms-auto success-message', { invisible: membersAdded <= 0 })}>
                                <VerbText
                                    messageId={'COMPANY_MEMBERS_ADDED'}
                                    defaultMessage={'{MemberCount} members have been added to your organization.'}
                                    valuesToInject={{ MemberCount: membersAdded }}
                                />
                            </div>
                            <Field
                                name={'memberIds'}
                                className={'col-12'}
                                disabled={updatingMembers}
                                options={this.getMemberOptions()}
                                onChange={this.addMember}
                                component={BasicSelect}
                                isMulti={true}
                                menuBuffer={15}
                            />
                            <br />
                        </div>
                    ) : null}
                    <div className={'col-sm-12 tabListControl company-tab-list'}>
                        <ul className={`col-12 tabList rightTabAlignment`}>
                            <li
                                className={cns({ 'active-tab': selectedTab === `registered` })}
                                onClick={this.setTab('registered')}
                            >
                                REGISTERED
                                <strong> ({memberCounts.members})</strong>
                            </li>
                            {enableInviteByEmail && (
                                <li
                                    className={cns({ 'active-tab': selectedTab === `invited` })}
                                    onClick={this.setTab('invited')}
                                >
                                    INVITED
                                    <strong> ({memberCounts.awaitingRegistration})</strong>
                                </li>
                            )}
                        </ul>
                    </div>
                    {selectedTab === 'registered' && (
                        <Fragment>
                            <RegisteredMembersTab
                                removeMembersFromCompany={this.removeMembersFromCompany}
                                registeredMembers={registeredMembers}
                                company={company}
                                updatingMembers={updatingMembers}
                                isVerbAdmin={isVerbAdmin}
                                handleCompanyMemberSearch={this.handleCompanyMemberSearch}
                                handleClearSearchBar={this.clearSearch}
                                searchHasEmptyResults={searchHasEmptyResults}
                                totalMemberCount={memberCounts.members}
                            />
                            <div className={'col-12'}>
                                {registeredMembers.length >= 10 ? (
                                    <PaginatedFetch
                                        limit={10}
                                        currentPage={this.state[`${selectedTab}Page`]}
                                        onlyLoadMoreOnce
                                        fetchMoreFunc={
                                            hasSearchFilter
                                                ? this.getMoreSearchedCompanyMemberProfiles
                                                : this.getCompanyMembersByTabName
                                        }
                                    />
                                ) : null}
                            </div>
                        </Fragment>
                    )}
                    {selectedTab === 'invited' && (
                        <Fragment>
                            <InvitedMembersTab
                                sendEmailReminders={this.sendEmailReminders}
                                invitedMembers={invitedMembers}
                                totalInvitedMembersCount={memberCounts.invited}
                                company={company}
                                emailSentCount={emailSentCount}
                                updatingMembers={updatingMembers}
                                handleCompanyMemberSearch={this.handleCompanyMemberSearch}
                                handleClearSearchBar={this.clearSearch}
                                searchHasEmptyResults={searchHasEmptyResults}
                                realDeleteProfile={realDeleteProfile}
                                getCompanyMembersByTabName={this.getCompanyMembersByTabName}
                            />
                            <div className={'col-12'}>
                                {invitedMembers.length >= 10 ? (
                                    <PaginatedFetch
                                        limit={10}
                                        currentPage={this.state[`${selectedTab}Page`]}
                                        onlyLoadMoreOnce
                                        fetchMoreFunc={
                                            hasSearchFilter
                                                ? this.getMoreSearchedCompanyMemberProfiles
                                                : this.getCompanyMembersByTabName
                                        }
                                    />
                                ) : null}
                            </div>
                        </Fragment>
                    )}
                </form>
            </div>
        );
    }
}

export const CompanyEmployeesFormComponent = CompanyEmployeesForm;

const getCompanyValues = getFormValues('CompanyEmployees');
const getInviteFormValues = getFormValues('Invite');

export const makeMapStateToProps = () => {
    const getAllProfilesFromStateById = makeGetObjectsFromStateById('profiles');

    const mapStateToProps = (state, ownProps) => {
        const { licenseNumber } = ownProps.company;
        const {
            profiles: { byId }
        } = state;
        const memberIds = []; // should always be empty as this is used to add new employees.

        const { company = {} } = ownProps;

        return {
            dirty: isDirty('CompanyEmployees'),
            currentValues: getCompanyValues(state),
            currentEmployeeInviteFormValues: getInviteFormValues(state),
            allProfiles: getAllProfilesFromStateById(state),
            profilesById: byId,
            licenseNumber,
            initialValues: {
                memberIds
            },
            company
        };
    };

    return mapStateToProps;
};

const mapDispatchToProps = (dispatch) => ({
    inviteEmployees: (params) => dispatch(inviteEmployees(params)),
    putCompany: (params) => dispatch(putCompany(params)),
    putCompanyMembers: (params) => dispatch(putCompanyMembers(params)),
    getManyProfiles: (params) => dispatch(getManyProfiles(params)),
    showModal: (params) => dispatch(showModal(params)),
    hideModal: (params) => dispatch(hideModal(params)),
    clearSearchedProfiles: (params) => dispatch(clearSearchedProfiles(params)),
    postSearchedCompanyMemberProfiles: (params) => dispatch(postSearchedCompanyMemberProfiles(params)),
    realDeleteProfile: (params) => dispatch(realDeleteProfile(params)),
    changeFormValue: (params) => dispatch(change('CompanyEmployees', params.field, params.value)),
    getManyTeamsAdmin: (params) => dispatch(getManyTeamsAdmin(params)),
    changeSearchFormValue: (params) => dispatch(change('SearchForm', params.field, params.value))
});

const CompanyEmployeesFormValidation = {
    form: 'CompanyEmployees',
    validate: (values) => {
        const errors = {};
        if (!values.emails) {
            errors.emails = 'At least one email required';
        }
        return errors;
    },
    destroyOnUnmount: false,
    enableReinitialize: true
};

export default compose(
    connect(makeMapStateToProps, mapDispatchToProps),
    reduxForm(CompanyEmployeesFormValidation)
)(CompanyEmployeesForm);
